import { useState } from "react";
import SelectMenu, { type SelectMenuOption } from "./SelectMenu";
import {
  describeCron,
  parseCron,
  formToCron,
  isValidCron,
  pad2,
  utcToLocalHHMM,
  WEEKDAY_LABELS,
  SCHEDULE_MINUTES,
} from "../../utils";
import type { Frequency, SchedForm } from "../../utils";

const FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

const HOUR_OPTIONS: SelectMenuOption[] = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${pad2(i)}:00`,
}));

const MINUTE_OPTIONS: SelectMenuOption[] = SCHEDULE_MINUTES.map((m) => ({
  value: String(m),
  label: pad2(m),
}));

const DAY_OF_MONTH_OPTIONS: SelectMenuOption[] = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

type SchedulePickerProps = {
  value: string;
  onChange: (cron: string) => void;
};

export default function SchedulePicker({ value, onChange }: SchedulePickerProps) {
  const [form, setForm] = useState<SchedForm>(() => parseCron(value));

  const cronValue = formToCron(form);
  const human = describeCron(cronValue);

  const utcHHMM = `${String(form.hour).padStart(2, "0")}:${String(form.minute).padStart(2, "0")}`;
  const localHHMM = utcToLocalHHMM(form.hour, form.minute);
  const showLocal = form.frequency !== "custom" && localHHMM !== utcHHMM;
  const customInvalid = form.frequency === "custom" && form.customCron.trim() !== "" && !isValidCron(form.customCron);

  function update(patch: Partial<SchedForm>) {
    const next = { ...form, ...patch };
    setForm(next);
    onChange(formToCron(next));
  }

  function toggleDay(idx: number) {
    const next = form.days.includes(idx)
      ? form.days.filter((d) => d !== idx)
      : [...form.days, idx];
    if (next.length === 0) return;
    update({ days: next });
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {FREQUENCIES.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => update({ frequency: f.value })}
            className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              form.frequency === f.value
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {form.frequency !== "custom" && (
        <div className="flex items-end gap-3">
          <div className="w-28">
            <label className="mb-1 block text-xs font-semibold text-gray-700">Hour (UTC)</label>
            <SelectMenu value={String(form.hour)} onChange={(v) => update({ hour: Number(v) })} options={HOUR_OPTIONS} />
          </div>
          <div className="w-24">
            <label className="mb-1 block text-xs font-semibold text-gray-700">Minute</label>
            <SelectMenu value={String(form.minute)} onChange={(v) => update({ minute: Number(v) })} options={MINUTE_OPTIONS} />
          </div>
          {form.frequency === "monthly" && (
            <div className="w-32">
              <label className="mb-1 block text-xs font-semibold text-gray-700">Day of month</label>
              <SelectMenu value={String(form.dayOfMonth)} onChange={(v) => update({ dayOfMonth: Number(v) })} options={DAY_OF_MONTH_OPTIONS} />
            </div>
          )}
        </div>
      )}

      {form.frequency === "weekly" && (
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-gray-700">Days</label>
          <div className="flex gap-1.5">
            {WEEKDAY_LABELS.map((day, idx) => (
              <button
                key={day}
                type="button"
                onClick={() => toggleDay(idx)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  form.days.includes(idx)
                    ? "bg-blue-600 text-white"
                    : "border border-gray-300 bg-white text-gray-600 hover:border-gray-400"
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}

      {form.frequency === "custom" && (
        <div>
          <input
            value={form.customCron}
            onChange={(e) => update({ customCron: e.target.value })}
            placeholder="e.g. 0 8 * * 1-5"
            className={`w-full rounded-lg border bg-white px-3 py-2 font-mono text-sm text-gray-900 focus:outline-none focus:ring-1 ${
              customInvalid
                ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            }`}
          />
          {customInvalid && <p className="mt-1 text-xs text-red-600">Invalid cron expression</p>}
        </div>
      )}

      <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
        Runs: <span className="font-medium text-gray-800">{human ?? (cronValue ? "Custom schedule" : "—")}</span>
        {showLocal && <span className="text-gray-400"> · ≈ {localHHMM} your time</span>}
      </p>
    </div>
  );
}
